import type { StateAction } from "@entrilia/shared/types";
import { generateGuid } from "@entrilia/shared/utilities/generateGuid";
import type { Field } from "../../../../../../api/types/objectTypes";
import type {
  ChangeSetUpdate,
  NewRelatedChangeSetConfiguration,
  RelatedChangeSetConfiguration,
  UpdateWorkflowActivityChangeSetsRequest,
  WorkflowActivity,
  WorkflowActivityChangeSet,
  WorkflowActivityChangeSetAction,
  WorkflowActivityChangeSetField,
  WorkflowActivityChangeSetSection,
  WorkflowActivityEditConfiguration,
} from "../../../../../../api/types/workflowTypes";
import { getMainChangeSet, isVisibleField } from "../workflowDetailsHelpers";

export interface ChangeSetEditState {
  fields: FieldState[];
  relatedChangeSets: RelatedChangeSetEditGroup[];
  isDirty: boolean;
  isValid: boolean;
}

export interface RelatedChangeSetEditGroup {
  name: string;
  displayFieldDefinitions: Field[];
  configuration: RelatedChangeSetConfiguration;
  newChangeSetTemplate?: NewRelatedChangeSetConfiguration;
  rows: RelatedChangeSetEditRow[];
}

export interface RelatedChangeSetEditRow {
  changeSetId: string;
  isNew: boolean;
  name: string;
  action: WorkflowActivityChangeSetAction;
  objectType: string;
  objectId?: string;
  entityTypeCode?: string;
  fields: FieldState[];
}

export interface FieldState extends WorkflowActivityChangeSetField {
  hasError: boolean;
  isDirty: boolean;
}

export interface ChangeSetFieldGroup {
  name?: string;
  fields: FieldState[];
}

const isReadOnlyField = (field: { attributes?: string[] }): boolean => field.attributes?.includes("ReadOnly") ?? false;

const isFieldInvalid = (field: WorkflowActivityChangeSetField): boolean => {
  if (!field.attributes?.includes("Required")) {
    return false;
  }

  const isValueMissing =
    field.newValue === undefined ||
    field.newValue === null ||
    (typeof field.newValue === "string" && field.newValue.trim() === "");

  return isValueMissing;
};

export const getMissingRequiredFieldCaptions = (fields: WorkflowActivityChangeSetField[]): string[] =>
  fields.filter(isFieldInvalid).map((field) => field.caption);

const createFieldState = (field: WorkflowActivityChangeSetField, isDirty: boolean): FieldState => ({
  ...field,
  hasError: isFieldInvalid(field),
  isDirty,
});

const createFieldStateFromDefinition = (fieldDefinition: Field): FieldState =>
  createFieldState(
    {
      name: fieldDefinition.id,
      caption: fieldDefinition.name,
      fieldType: fieldDefinition.type,
      fieldConfiguration: fieldDefinition.configuration,
      attributes: fieldDefinition.attributes,
    },
    true
  );

const createSyntheticFieldState = (fieldName: string): FieldState =>
  createFieldState(
    {
      name: fieldName,
      caption: fieldName,
      fieldType: "Text",
    },
    true
  );

const getWritableFields = (fields: WorkflowActivityChangeSetField[]): FieldState[] =>
  fields.filter((field) => !isReadOnlyField(field)).map((field) => createFieldState(field, false));

const getConfiguredFieldNames = (configuration: RelatedChangeSetConfiguration): string[] => [
  ...configuration.hiddenFieldNames,
  ...configuration.objectSelectConfigurations.flatMap((objectSelectConfiguration) => [
    objectSelectConfiguration.fieldName,
    ...objectSelectConfiguration.additionalFields.map((additionalField) => additionalField.changeSetFieldName),
  ]),
];

const createNewRelatedChangeSetFields = (group: RelatedChangeSetEditGroup): FieldState[] => {
  const fields = group.displayFieldDefinitions
    .filter((fieldDefinition) => !isReadOnlyField(fieldDefinition))
    .map(createFieldStateFromDefinition);

  const existingFieldNames = new Set(fields.map((field) => field.name));

  for (const fieldName of getConfiguredFieldNames(group.configuration)) {
    if (!existingFieldNames.has(fieldName)) {
      fields.push(createSyntheticFieldState(fieldName));
      existingFieldNames.add(fieldName);
    }
  }

  return fields;
};

const createNewRelatedChangeSetRow = (group: RelatedChangeSetEditGroup): RelatedChangeSetEditRow => {
  if (!group.newChangeSetTemplate) {
    throw new Error(`Related change set group '${group.name}' does not have new change set metadata.`);
  }

  return {
    changeSetId: generateGuid(),
    isNew: true,
    name: group.newChangeSetTemplate.name,
    action: group.newChangeSetTemplate.action,
    objectType: group.newChangeSetTemplate.objectType,
    entityTypeCode: group.newChangeSetTemplate.entityTypeCode,
    fields: createNewRelatedChangeSetFields(group),
  };
};

const isValidState = (fields: FieldState[], relatedChangeSets: RelatedChangeSetEditGroup[]): boolean =>
  fields.every((field) => !field.hasError) &&
  relatedChangeSets.every((group) => group.rows.every((row) => row.fields.every((field) => !field.hasError)));

const refreshStateValidation = (state: ChangeSetEditState): ChangeSetEditState => ({
  ...state,
  isValid: isValidState(state.fields, state.relatedChangeSets),
});

const createRelatedChangeSetEditRow = (changeSet: WorkflowActivityChangeSet): RelatedChangeSetEditRow => ({
  changeSetId: changeSet.id,
  isNew: false,
  name: changeSet.name,
  action: changeSet.action,
  objectType: changeSet.objectType,
  objectId: changeSet.objectId,
  entityTypeCode: changeSet.entityTypeCode,
  fields: getWritableFields(changeSet.fields),
});

const getFallbackNewChangeSetTemplate = (
  configuration: RelatedChangeSetConfiguration,
  relatedChangeSets: WorkflowActivityChangeSet[]
): NewRelatedChangeSetConfiguration | undefined => {
  if (configuration.newChangeSet) {
    return configuration.newChangeSet;
  }

  const templateChangeSet = relatedChangeSets[0];
  if (!templateChangeSet) {
    return undefined;
  }

  return {
    name: templateChangeSet.name,
    action: templateChangeSet.action,
    objectType: templateChangeSet.objectType,
    entityTypeCode: templateChangeSet.entityTypeCode,
  };
};

const getInitialRelatedChangeSetEditGroups = (
  activityChangeSet: WorkflowActivityChangeSet,
  editConfiguration?: WorkflowActivityEditConfiguration
): RelatedChangeSetEditGroup[] => {
  const result: RelatedChangeSetEditGroup[] = [];

  for (const relatedChangeSets of activityChangeSet.relatedChangeSets ?? []) {
    const configuration = editConfiguration?.relatedChangeSets?.[relatedChangeSets.name];
    if (!configuration) {
      continue;
    }

    result.push({
      name: relatedChangeSets.name,
      displayFieldDefinitions: relatedChangeSets.displayFieldDefinitions,
      configuration,
      newChangeSetTemplate: getFallbackNewChangeSetTemplate(configuration, relatedChangeSets.changeSets),
      rows: relatedChangeSets.changeSets.map(createRelatedChangeSetEditRow),
    });
  }

  return result;
};

export const getInitialChangeSetEditState = (
  activityChangeSet: WorkflowActivityChangeSet,
  editConfiguration?: WorkflowActivityEditConfiguration
): ChangeSetEditState => {
  const fields = getWritableFields(activityChangeSet.fields);
  const relatedChangeSets = getInitialRelatedChangeSetEditGroups(activityChangeSet, editConfiguration);
  const isValid = isValidState(fields, relatedChangeSets);

  return { fields, relatedChangeSets, isDirty: false, isValid };
};

export const getInitialInlineChangeSetEditState = (
  activity: WorkflowActivity,
  canEdit: boolean
): ChangeSetEditState | undefined => {
  if (
    !canEdit ||
    activity.type !== "ExtractFieldsFromDocument" ||
    activity.state !== "WaitingForSignal" ||
    activity.workflowActivityId === undefined ||
    activity.userEditConfiguration === undefined
  ) {
    return undefined;
  }

  const changeSet = getMainChangeSet(activity);
  if (!changeSet || changeSet.fields.length === 0) {
    return undefined;
  }

  return getInitialChangeSetEditState(changeSet, activity.userEditConfiguration);
};

export const groupFieldsBySections = (
  fields: FieldState[],
  sections: WorkflowActivityChangeSetSection[] | undefined
): ChangeSetFieldGroup[] => {
  const visibleFields = fields.filter(isVisibleField);
  if (!sections || sections.length === 0) {
    return visibleFields.length > 0 ? [{ fields: visibleFields }] : [];
  }

  const fieldsByName = new Map(visibleFields.map((field) => [field.name, field]));
  const sectionedNames = new Set<string>();
  const sectionGroups: ChangeSetFieldGroup[] = [];

  for (const section of sections) {
    const sectionFields: FieldState[] = [];
    for (const sectionField of section.fields) {
      sectionedNames.add(sectionField.name);
      const field = fieldsByName.get(sectionField.name);
      if (field) {
        sectionFields.push(field);
      }
    }

    if (sectionFields.length > 0) {
      sectionGroups.push({ name: section.name, fields: sectionFields });
    }
  }

  const ungroupedFields = visibleFields.filter((field) => !sectionedNames.has(field.name));
  return ungroupedFields.length > 0 ? [{ fields: ungroupedFields }, ...sectionGroups] : sectionGroups;
};

const mapFieldsToUpdates = (fields: FieldState[]) =>
  fields.map((field) => ({
    name: field.name,
    valueUserInput: field.newValue ?? null,
  }));

const mapUnconfiguredRelatedChangeSetUpdates = (
  activityChangeSet: WorkflowActivityChangeSet,
  configuredRelatedChangeSetNames: Set<string>
): Record<string, ChangeSetUpdate> => {
  const result: Record<string, ChangeSetUpdate> = {};

  for (const relatedChangeSets of activityChangeSet.relatedChangeSets ?? []) {
    if (configuredRelatedChangeSetNames.has(relatedChangeSets.name)) {
      continue;
    }

    for (const changeSet of relatedChangeSets.changeSets) {
      result[changeSet.id] = {
        fieldUpdates: mapFieldsToUpdates(getWritableFields(changeSet.fields)),
      };
    }
  }

  return result;
};

export const mapStateToUpdateWorkflowActivityChangeSetsRequest = (
  editState: ChangeSetEditState,
  activityChangeSet: WorkflowActivityChangeSet
): UpdateWorkflowActivityChangeSetsRequest => {
  const configuredRelatedChangeSetNames = new Set(editState.relatedChangeSets.map((group) => group.name));
  const changeSetUpdates: Record<string, ChangeSetUpdate> = {
    ...mapUnconfiguredRelatedChangeSetUpdates(activityChangeSet, configuredRelatedChangeSetNames),
    [activityChangeSet.id]: {
      fieldUpdates: mapFieldsToUpdates(editState.fields),
    },
  };

  for (const group of editState.relatedChangeSets) {
    for (const row of group.rows) {
      changeSetUpdates[row.changeSetId] = {
        fieldUpdates: mapFieldsToUpdates(row.fields),
        newChangeSetMetadata: row.isNew
          ? {
              relatedChangeSetsName: group.name,
              name: row.name,
              action: row.action,
              objectType: row.objectType,
              objectId: row.objectId,
              entityTypeCode: row.entityTypeCode,
            }
          : undefined,
      };
    }
  }

  return { changeSetUpdates };
};

// Actions

export const updateChangeSetFieldAction =
  (fieldName: string, newValue: unknown): StateAction<ChangeSetEditState> =>
  (state) =>
    updateChangeSetFieldsAction({ [fieldName]: newValue })(state);

export const updateChangeSetFieldsAction =
  (fieldUpdates: Record<string, unknown>): StateAction<ChangeSetEditState> =>
  (state) => {
    const updatedFieldNames = new Set(Object.keys(fieldUpdates));
    const fields = state.fields.map((field) => {
      if (!updatedFieldNames.has(field.name)) {
        return field;
      }

      const updatedField = { ...field, newValue: fieldUpdates[field.name] };
      const hasError = isFieldInvalid(updatedField);
      return { ...updatedField, hasError, isDirty: true };
    });

    return refreshStateValidation({
      ...state,
      fields,
      isDirty: true,
    });
  };

export const updateRelatedChangeSetFieldsAction =
  (groupName: string, changeSetId: string, fieldUpdates: Record<string, unknown>): StateAction<ChangeSetEditState> =>
  (state) => {
    const updatedFieldNames = new Set(Object.keys(fieldUpdates));
    const relatedChangeSets = state.relatedChangeSets.map((group) => {
      if (group.name !== groupName) {
        return group;
      }

      const rows = group.rows.map((row) => {
        if (row.changeSetId !== changeSetId) {
          return row;
        }

        const fields = row.fields.map((field) => {
          if (!updatedFieldNames.has(field.name)) {
            return field;
          }

          const updatedField = { ...field, newValue: fieldUpdates[field.name] };
          const hasError = isFieldInvalid(updatedField);
          return { ...updatedField, hasError, isDirty: true };
        });

        return { ...row, fields };
      });

      return { ...group, rows };
    });

    return refreshStateValidation({
      ...state,
      relatedChangeSets,
      isDirty: true,
    });
  };

export const addRelatedChangeSetRowAction =
  (groupName: string): StateAction<ChangeSetEditState> =>
  (state) => {
    const targetGroup = state.relatedChangeSets.find((group) => group.name === groupName);
    if (!targetGroup?.newChangeSetTemplate) {
      return state;
    }

    const relatedChangeSets = state.relatedChangeSets.map((group) =>
      group.name === groupName ? { ...group, rows: [...group.rows, createNewRelatedChangeSetRow(group)] } : group
    );

    return refreshStateValidation({
      ...state,
      relatedChangeSets,
      isDirty: true,
    });
  };

export const removeRelatedChangeSetRowAction =
  (groupName: string, changeSetId: string): StateAction<ChangeSetEditState> =>
  (state) => {
    const relatedChangeSets = state.relatedChangeSets.map((group) => {
      if (group.name !== groupName) {
        return group;
      }

      return { ...group, rows: group.rows.filter((row) => row.changeSetId !== changeSetId) };
    });

    return refreshStateValidation({
      ...state,
      relatedChangeSets,
      isDirty: true,
    });
  };
